import { usePageTitle } from '../hooks/usePageTitle';

const CONTACT = 'Chuck@chuckgiddens.com';

export default function Privacy() {
  usePageTitle("Privacy Policy — Chuck's Mojito Shack");

  return (
    <div className="container page-content py-5" style={{ maxWidth: '760px' }}>
      <h1 className="mb-1">Privacy Policy</h1>
      <p className="text-muted mb-4">
        <small>Last updated: October 5, 2026</small>
      </p>

      <p>
        Chuck's Mojito Shack is this website and the Chuck's Mojitos app for iPhone and Android, run by Chuck
        Giddens for the Towne Lake Hills men's tennis team. The bar is a joke; this policy is not. It says what we
        collect, why, who else touches it, and how to make it go away.
      </p>

      <h2 className="mt-4">The short version</h2>
      <ul>
        <li>We collect only what the team pages need to work: who you are on the roster, and what you post.</li>
        <li>No ads, no tracking across other apps or sites, no selling or renting your data. Ever.</li>
        <li>
          You can remove your account from the app (Profile → Remove my account) or email{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a> to have your data deleted.
        </li>
      </ul>

      <h2 className="mt-4">What we collect</h2>
      <p>
        <strong>Anyone can browse</strong> the home page, the menu and the mojito simulator without giving us
        anything. When a page loads, the site records which page was viewed, the referring page and the time, so
        Chuck can see what gets used. That record carries no name or email.
      </p>
      <p>
        <strong>Signing in to the app.</strong> On iPhone anyone can follow the Team and Kourt tabs without signing
        in, and nothing about a guest is stored. Players sign in to post, with Apple (iPhone) or Google (Android). Apple or Google tells us your email address and an account
        identifier; we check the email against the roster and keep your roster name with your session. If you use
        Apple's "Hide My Email", we receive the relay address, not your real one. We never see your Apple or Google
        password.
      </p>
      <p>
        <strong>The team roster</strong> (names, emails, mobile numbers and team roles) comes from our league's
        published roster and from Chuck. It is stored encrypted and is used only to recognise you at sign-in and to
        reach you about team business. It is never shown to other users of the app or the site.
      </p>
      <p>
        <strong>What you choose to post:</strong> a nickname, match scores, messages and their photos, emoji
        reactions, Kangaroo Kourt complaints (with an optional photo), and your IN/OUT answer for Thursday practice.
        Teammates who use the app and the team pages can see these posts.
      </p>
      <p>
        <strong>Your mobile number, if you give it</strong> on the Kourt's opt-in form, is used only to text you
        about a fine.
      </p>
      <p>
        <strong>Photos.</strong> The app asks for camera or photo-library access only when you attach a photo, and
        uploads only the photo you pick, shrunk to a small JPEG.
      </p>
      <p>
        <strong>Notifications.</strong> Practice reminders are scheduled on your phone. No push server is involved
        and no device identifier is sent to us.
      </p>

      <h2 className="mt-4">How we use it</h2>
      <p>
        To run the team pages: show the scoreboard, messages and the Kourt, email registered players when scores or
        messages are posted, email (and, if you opted in, text) a player about a Kourt ruling, and remind the
        practice group to answer. That is the whole list.
      </p>

      <h2 className="mt-4">Who else handles it</h2>
      <ul>
        <li>
          <strong>Microsoft Azure</strong> (United States) hosts the site, the app's server and its storage, and sends
          our texts (Azure Communication Services).
        </li>
        <li>
          <strong>DTS Mail</strong>, Chuck's own mail service (also hosted on Azure in the United States), sends our
          emails from team@chucksmojitos.com and kourt@chucksmojitos.com.
        </li>
        <li>
          <strong>Apple and Google</strong> handle sign-in. Their own privacy policies apply to your Apple ID or
          Google account.
        </li>
        <li>
          <strong>Open-Meteo</strong> supplies the match-day forecast. We send it the courts' location, never yours.
        </li>
      </ul>
      <p>
        We do not sell, rent or share personal data with advertisers or data brokers, and nothing in the site or the
        app tracks you across other companies' apps or websites.
      </p>

      <h2 className="mt-4">Cookies and local storage</h2>
      <p>
        The site keeps your nickname and email in your browser's local storage after you register, so it recognises
        you next time, plus the usual sign-in cookies if you log in. No advertising or analytics cookies.
      </p>

      <h2 className="mt-4">How long we keep it</h2>
      <p>
        Posts and the Kourt's record stay for the season and its history, because that is the point of a
        scoreboard. Removing your account in the app signs you out, stops the team emails, and keeps you out until you choose
        to rejoin.
        To have your posts, photos, phone number or roster details deleted, email us and we will do it within 30
        days.
      </p>

      <h2 className="mt-4" id="community-rules">Community rules</h2>
      <p>
        Trash talk is welcome; abuse is not. By posting on the team board or filing a Kourt complaint you agree to keep it
        to that: no slurs or hate, no harassment or threats, no sexual content, nothing illegal, and nothing that
        shares someone's private information. There is zero tolerance for objectionable content or abusive users.
      </p>
      <ul>
        <li>Posts with slurs are refused before they are published.</li>
        <li>
          In the app, press and hold a message or a complaint to <strong>Report</strong> it, or to <strong>Block</strong>{' '}
          the player who posted it (you stop seeing their posts; unblock any time from your Profile).
        </li>
        <li>
          Every report goes to the moderator, who reviews it within 24 hours, removes content that breaks these rules,
          and removes players who keep breaking them.
        </li>
      </ul>

      <h2 className="mt-4">Children</h2>
      <p>
        The site and the app are for an adult tennis league and are not directed to children under 13. We do not
        knowingly collect their information.
      </p>

      <h2 className="mt-4">Your choices</h2>
      <ul>
        <li>Browse without signing in, or sign in and never post.</li>
        <li>Skip the phone number; the Kourt will only email you.</li>
        <li>Turn reminders off in your phone's notification settings.</li>
        <li>Remove your account in the app, or ask us for a copy of your data, a correction, or deletion.</li>
      </ul>

      <h2 className="mt-4">Changes</h2>
      <p>If this policy changes, the date at the top changes with it.</p>

      <h2 className="mt-4">Contact</h2>
      <p>
        Questions or requests: <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. Or find Chuck at the courts on a
        Saturday — the first round is on him.
      </p>
    </div>
  );
}
